import React, { useState } from 'react';
import { Row, Col, Tag, Checkbox } from 'antd';
import { Link } from 'react-router-dom';

// components
import CustomAvatar from '../shared/avatar/CustomAvatar';
import { useEntityRegistry } from '../useEntityRegistry';
import CustomTooltip from '../entity/shared/components/common/CustomTooltip';
import { StyledTable } from '../entity/shared/components/styled/StyledTable';
import { AutoTooltip } from '../entity/shared/components/common/AutoTooltip';

// generated
import { BrowseDataProductRelatedEntityType, DataDataProductTable } from './BrowseDataProductTableType';
import BrowseDataProductTableExplorer from './BrowseDataProductTableExplorer';
import { ComponentSpinner } from '../shared/ComponentSpinner';
import { useTranslation } from '../../i18n/useTranslation';
import { EntityType } from '../../types.generated';

export interface DataProductTableProps {
    data: DataDataProductTable[] | undefined;
    onModalOpen: (key: string, dataProductUrn?: string, defaultValues?: BrowseDataProductRelatedEntityType[]) => void;
    onSelectAll: (e) => void;
    onSelectItem: (dpName: string, e) => void;
    selectedItems: string[];
    isAllSelected: boolean;
    loading: boolean;
}

export const BrowseDataProductTable: React.FC<DataProductTableProps> = ({
    data,
    onModalOpen,
    onSelectItem,
    onSelectAll,
    selectedItems = [],
    loading,
    isAllSelected,
}: DataProductTableProps) => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const [dropDownVisible, setDropdownVisible] = useState<number>(-1);
    const dataProductGird = [
        {
            width: '4%',
            title: (
                <Checkbox
                    aria-label={t('Select All')}
                    onChange={onSelectAll}
                    disabled={!data?.length}
                    checked={isAllSelected}
                    indeterminate={!isAllSelected && selectedItems.length > 0}
                />
            ),
            ariaLabel: t('Select All'),
            dataIndex: 'selected',
            key: 'selected',
            render: (_, record: DataDataProductTable) => (
                <Checkbox
                    checked={selectedItems?.includes(record.id)}
                    className="ml-2"
                    aria-label="Select this Row"
                    onChange={(e) => onSelectItem(record.id, e)}
                />
            ),
        },
        {
            width: '40%',
            title: t('Data Product / Domain Hierarchy'),
            ariaLabel: t('Data Product'),
            dataIndex: 'dataProduct',
            key: 'dataProduct',
            ellipsis: { showTitle: false },
            render: (_, record: DataDataProductTable) => {
                const url = entityRegistry.getEntityUrl(EntityType.DataProduct, record.dataProduct.urn);
                return (
                    <AutoTooltip role="button" tabIndex={0}>
                        <Link
                            className="f-text-medium-content f-text-single-line f-text-semibold f-color-dark-black-s100"
                            role="button"
                            tabIndex={0}
                            to={url}
                        >
                            {record.dataProduct.name}
                        </Link>
                        <div className="f-text-medium-content f-color-dark-black-s70">
                            {record.domain.path ? `${record.domain.path} / ` : ''} {record.domain.name}
                        </div>
                    </AutoTooltip>
                );
            },
        },
        {
            width: '15%',
            title: t('Glossary Terms'),
            ariaLabel: t('Glossary Terms'),
            dataIndex: 'glossaryTerms',
            key: 'glossaryTerms',
            render: (glossaryTerms: BrowseDataProductRelatedEntityType[]) => {
                if (!glossaryTerms || !Array.isArray(glossaryTerms)) return null;
                return (
                    <Row wrap={false} gutter={4} align="middle" className="mx-0">
                        <Col flex="none">
                            {glossaryTerms[0] ? (
                                <Tag className="label-tag grey-tag-light ma-0">{glossaryTerms[0]?.name}</Tag>
                            ) : (
                                <span>-</span>
                            )}
                        </Col>
                        {glossaryTerms.length > 1 && (
                            <Col flex="none">
                                <CustomTooltip items={glossaryTerms.map((item) => item.name)} />
                            </Col>
                        )}
                    </Row>
                );
            },
        },
        {
            width: '13%',
            title: t('Tags'),
            ariaLabel: t('Tags'),
            dataIndex: 'tags',
            key: 'tags',
            render: (tags: BrowseDataProductRelatedEntityType[]) => {
                if (!tags || !Array.isArray(tags)) return null;
                return (
                    <Row wrap={false} gutter={4} align="middle" className="mx-0">
                        <Col flex="none">
                            {tags[0] ? (
                                <Tag className="label-tag grey-tag-light ma-0">{tags[0]?.name}</Tag>
                            ) : (
                                <span>-</span>
                            )}
                        </Col>
                        {tags.length > 1 && (
                            <Col flex="none">
                                <CustomTooltip items={tags.map((item) => item.name)} />
                            </Col>
                        )}
                    </Row>
                );
            },
        },
        {
            width: '10%',
            title: t('Assets'),
            ariaLabel: t('Assets'),
            dataIndex: 'assets',
            key: 'assets',
            render: (assets: number) => {
                return <div className="f-left-border-highlight f-height-auto py-0">{assets ?? '-'}</div>;
            },
        },
        {
            title: t('Owner'),
            width: '6%',
            ariaLabel: t('Owner'),
            dataIndex: 'owner',
            key: 'owner',
            render: (owner: BrowseDataProductRelatedEntityType[]) => {
                if (!owner || !Array.isArray(owner)) return null;
                return (
                    <Row wrap={false} align="middle" className="mx-0">
                        <Col flex="none">
                            {owner[0] ? (
                                <CustomAvatar
                                    name={owner[0]?.name}
                                    email={(owner[0]?.entity as any)?.username}
                                    size={24}
                                />
                            ) : (
                                <span>-</span>
                            )}
                        </Col>
                        {owner.length > 1 && (
                            <Col flex="none">
                                <CustomTooltip items={owner.map((item) => item.name)} />
                            </Col>
                        )}
                    </Row>
                );
            },
        },
        {
            align: 'right',
            render: (_, record: DataDataProductTable, index: number) => (
                <BrowseDataProductTableExplorer
                    record={record}
                    isDropDownVisible={dropDownVisible === index}
                    index={index}
                    entityRegistry={entityRegistry}
                    onModalOpen={onModalOpen}
                    setDropdownVisible={(value: number) => setDropdownVisible(value)}
                />
            ),
        },
    ];

    return (
        <>
            {loading && <ComponentSpinner />}
            <StyledTable
                size="small"
                pagination={false}
                rowKey="glossaryGrid"
                loading={false}
                dataSource={data}
                // typescript is complaining that default sort order is not a valid column field- overriding this here
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                columns={dataProductGird}
            />
        </>
    );
};
